import { Button, Divider, LoadingOverlay, Modal, NumberInput, Radio, rem, Stack, Text } from "@mantine/core";
import React, { useState } from "react";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconTrophyFilled } from "@tabler/icons-react";
import { Position } from "../models";
import { useQuery } from "react-query";
import axios from "axios";
import { FaChessBoard } from "react-icons/fa6";
import { useSettingsStorage } from "../ZustandState";

interface Props {
    opened: boolean;
    close: () => void;
    position: Position;
    onResetPosition: (position: Position, shouldDeleteGames: boolean) => void;
}

const getNumAttemptsForPosition = async (position: Position) => {
    const res = await axios.get(`${import.meta.env.VITE_OPENING_TRAINER_BACKEND_URL}/ot_num_attempts_for_position`, {
        withCredentials: true,
        params: {
            positionId: position.id,
        },
    });
    return res.data.attempts;
};

const ResetPositionModal: React.FC<Props> = ({ opened, close, position, onResetPosition }) => {
    const settings = useSettingsStorage((state) => state);
    const { data: numAttempts, isLoading } = useQuery(["numAttemptsForPosition"], () =>
        getNumAttemptsForPosition(position)
    );
    const [startRating, setStartRating] = useState(settings.startRating);
    const [shouldDeleteGames, setShouldDeleteGames] = useState(true);

    const handleOnSetStartRating = (value: number) => {
        setStartRating(value);
        settings.setSettings({ ...settings, startRating: value });
    };

    if (isLoading) {
        return <LoadingOverlay />;
    }

    return (
        <Modal
            opened={opened}
            onClose={close}
            title={
                <Text fz="h3" fw="bold">
                    Reset rating of {position.name}?
                </Text>
            }
            centered
            padding={20}
            size="lg"
        >
            <NumberInput
                mb="xl"
                value={startRating}
                hideControls
                label="Enter new rating for this position"
                onChange={(value) => handleOnSetStartRating(parseInt(value as string))}
            />
            <div className="">
                <Radio.Group
                    mt="xl"
                    value={shouldDeleteGames ? "shouldDelete" : "shouldKeep"}
                    onChange={(value) => setShouldDeleteGames(value === "shouldDelete")}
                >
                    <Stack mt="xs">
                        <Radio value="shouldDelete" label="I also want to delete all games for this position" />
                        <Radio value="shouldKeep" label="Only reset the rating" />
                    </Stack>
                </Radio.Group>
                <Divider mt="lg" mb="lg" />

                {shouldDeleteGames && (
                    <div className="ml-8 flex items-center">
                        <FaChessBoard size={30} />
                        <Text mx="0.5rem" fz="h2" fw="bolder" span>
                            {numAttempts} games
                        </Text>{" "}
                        <Text span fz="lg" fw="normal">
                            will be deleted
                        </Text>
                    </div>
                )}
                <div className="ml-8 flex items-center">
                    <IconTrophyFilled size={30} />

                    <Text ml="sm" span fz="lg" fw="normal">
                        Rating will be reset from
                        <Text ml="0.5rem" fz="h2" fw="bolder" span>
                            {position.rating}
                        </Text>
                        <Text mx="0.5rem" fz="h2" fw="bolder" span>
                            to
                        </Text>
                        <Text fz="h2" fw="bolder" span>
                            {settings.startRating}
                        </Text>
                    </Text>
                </div>
                <div className="flex justify-center items-center space-x-4">
                    <Button
                        className="mt-6"
                        color="white"
                        variant="outline"
                        h="3rem"
                        onClick={() => {
                            close();
                        }}
                        fullWidth
                    >
                        <span className="text-lg">Cancel</span>
                    </Button>
                    <Button
                        className="mt-6"
                        color="red"
                        h="3rem"
                        onClick={() => {
                            onResetPosition(position, shouldDeleteGames);
                            notifications.show({
                                title: "Rating has been reset",
                                message: "",
                                icon: <IconCheck style={{ width: rem(18), height: rem(18) }} />,
                                color: "green",
                            });
                            close();
                        }}
                        fullWidth
                    >
                        <span className="text-lg">Reset rating</span>
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ResetPositionModal;
